/**
 * Utility functions for application-wide use
 */
// Removed top-level imports of redux actions to break circular dependencies
import { uploadToS3 } from "../pages/Canvas/utils/imageUploadUtils";

/**
 * Get logged-in user information from localStorage
 * @returns {Object|null} Parsed user info object or null if not found/invalid
 */
export const getLoggedInUserInfo = () => {
  try {
    const stored = localStorage.loggedinUserInfo;
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error("Failed to parse stored user info", error);
    return null;
  }
};

/**
 * Format a user's name for display
 * @param {Object} user - User object or string
 * @param {boolean} isApprover - Whether this is for approvers display
 * @returns {string} Formatted name
 */
export const formatName = (user, isApprover) => {
  if (!user) return "";

  if (typeof user === "string") return user;

  if (Array.isArray(user) && isApprover) {
    return user.map((u) => formatName(u)).join(", ");
  }

  const firstName = user.firstName || "";
  const lastName = user.lastName || "";

  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  } else if (user.name) {
    return user.name;
  } else if (user.email) {
    return user.email;
  }

  return user.id || "";
};

/**
 * Format EST time n the format: "YYYY-MM-DD, h:mmAM/PM EST"
 * ⚠️ Note: This function does NOT convert the input time from UTC to EST.
 * It assumes the input is already in EST and only formats it for display.
 * Use this only if you're certain the input time is EST.
 *
 * @param {string} dateString - ISO 8601 date string (e.g., "2025-05-13T13:05:00.000Z")
 * @returns {string} Formatted date string (e.g., "2025-05-13, 1:05PM EST")
 */
export const formatAsESTLabel = (dateString) => {
  const date = new Date(dateString);

  // Get components
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const isPM = hours >= 12;
  hours = hours % 12 || 12;

  const time = `${hours}:${minutes} ${isPM ? "PM" : "AM"} EST`;

  return `${year}-${month}-${day} at ${time}`;
};

/**
 * Format time with timezone label/conversion. 
 * Formats the UTC date string to the specified timezone time and appends the timezone label.
 * 
 * @param {string} dateString - ISO 8601 date string (UTC)
 * @param {string} timeZone - Timezone (EST, PST, UST, EDT). Default EST.
 * @returns {string} Formatted date string
 */
export const formatAsTimezoneLabel = (dateString) => {  
  if (!dateString) return "";
  const date = new Date(dateString);

  let mappedTz = "America/New_York";
  const tz = localStorage.getItem("application_time_zone");

  switch (tz) {
    case "PST":
      mappedTz = "America/Los_Angeles";
      break;
    case "UTC": // Treating as UTC
      mappedTz = "UTC";
      break;
    case "EST":
    case "EDT":
      mappedTz = "America/New_York";
      break;
    default:
      // Fallback or default to EST behavior
      mappedTz = "America/New_York";
  }

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: mappedTz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const parts = formatter.formatToParts(date);
    const getPart = (type) => parts.find((p) => p.type === type)?.value;

    const year = getPart("year");
    const month = getPart("month");
    const day = getPart("day");
    const hour = getPart("hour");
    const minute = getPart("minute");
    const dayPeriod = getPart("dayPeriod");

    return `${year}-${month}-${day} at ${hour}:${minute} ${dayPeriod} ${tz}`;
  } catch (error) {
    console.error("formatAsTimezoneLabel error:", error);
    return dateString;
  }
};

/**
 * Converts binary buffer data (array of bytes) into a Base64-encoded string.
 * This is typically used to display binary content like images in HTML as a data URL.
 *
 * @param {number[] | Uint8Array} bufferData - An array of byte values representing binary data (e.g., from a Node.js Buffer object or MongoDB's Buffer).
 *
 * @returns {string} A Base64-encoded string that can be embedded in a data URI (e.g., for use in <img src="...">).
 *
 * @example
 * const base64 = bufferToBase64(signature.data);
 * const imageSrc = `data:image/png;base64,${base64}`;
 * <img src={imageSrc} alt="Signature" />
 */
export function bufferToBase64(bufferData) {
  const uint8Array = new Uint8Array(bufferData);
  let binary = "";
  uint8Array.forEach((byte) => (binary += String.fromCharCode(byte)));
  return window.btoa(binary); // Convert binary string to base64
}

export const scrollToTop = (tableContainerRef) => {
  tableContainerRef?.current?.scrollTo({ top: 0, behavior: "smooth" });
};

// 2025-06-04T00:00:00.000Z to 2025/06/04 (with leading zeros)
export const formtDt = (date) => {
  if (!date) return "";

  // If date is already in YYYY-MM-DD or YYYY/MM/DD format, return as is
  if (typeof date === 'string' && date.includes('T')) {
    return date.split("T")[0].replace(/-/g, '/');
  }

  // Convert to Date object and format with leading zeros
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return ""; // Invalid date

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');

  return `${year}/${month}/${day}`;
};

// Add this component at the top of the file, after the imports
export const calcTotalNodesCount = (array) => {
  const count = Object.values(array).reduce((acc, arr) => {
    return acc + arr.length;
  }, 0);

  return count;
};

export const getAdjustedSiblingOrder = (parentOrder, children) => {
  // Safety check: ensure children is an array
  const childrenArray = children || [];

  const validSiblingOrders = childrenArray
    .map((child) => child.sibling_order || child.procedure_tree?.sibling_order)
    .filter(Boolean)
    .map((order) => parseInt(order, 10));

  if (validSiblingOrders.length === 0) {
    // No children, return parent + 1
    return `${parseInt(parentOrder) + 1}, 1`;
  }
  // Get the highest sibling order and increment it
  const highestOrder = Math.max(...validSiblingOrders);

  return `${parseInt(parentOrder) + 1}, ${(highestOrder + 1).toString()}`;
};

export function convertUtcToFormattedTime(utcString) {
  if (!utcString) return;

  // Import dynamically to avoid circular dependencies
  const { convertUtcToAppTimezone } = require("./timezoneUtils");

  // Get app setup state from Redux store
  let appSetupState = null;
  if (typeof window !== "undefined" && window.__REDUX_STORE__) {
    const state = window.__REDUX_STORE__.getState();
    appSetupState = state.appSetup;
  }

  return convertUtcToAppTimezone(utcString, appSetupState);
}

export function renderRevStatus(currentRev) {
  let status = "N/A";
  if (currentRev) {
    if (currentRev.status_changes) {
      let stat = currentRev.status_changes.find((s) => s.is_latest);
      status = stat.status_changed_to;
    }
  }
  return status;
}

export const ValuesTrimmer = (obj) => {
  const trimedojb = {};

  Object.keys(obj).forEach((key) => (trimedojb[key] = obj[key]?.trim()));

  return trimedojb;
};

/**
 * Generates a structured object containing section, step, substep,
 * and node heading information with dynamic keys.
 *
 * The keys are prefixed with the provided `fromTo` string, which is
 * commonly "from" or "to". This is useful when mapping relationships
 * between source and destination nodes/steps in workflows or tree structures.
 *
 * @param {string} fromTo - A prefix for the property names (e.g., "from" or "to").
 * @param {any} section - The section information.
 * @param {any} step - The step information.
 * @param {any} substep - The sub-step information----Note:pass null if no value.
 * @param {any} nodeHeading - The node heading information.
 */
export const getMoveCopyInfo = (
  fromTo,
  section,
  step,
  substep,
  nodeHeading
) => {
  return {
    [`${fromTo}Section`]: section,
    [`${fromTo}Step`]: step,
    [`${fromTo}SubStep`]: substep,
    [`${fromTo}NodeHeading`]: nodeHeading,
  };
};

export const getMoveCopyText = (
  moveCopyInfo,
  partNumber,
  rev,
  action = "move"
) => {
  const {
    fromSection,
    fromStep,
    fromSubStep,
    fromNodeHeading,
    toSection,
    toStep,
    toSubStep,
    toNodeHeading,
    direction,
  } = moveCopyInfo;
  let text = "";
  if (action === "move") {
    if (direction && direction !== "into") {
      text = `Moved ${fromNodeHeading} from ${fromSection}->${fromStep}${fromSubStep ? `->${fromSubStep}` : ""
        } into ${toSection}->${toStep}${toSubStep ? `->${toSubStep}` : ""
        } ${direction} ${toNodeHeading}`;
    } else if (direction === "into") {
      text = `Moved ${fromNodeHeading} from ${fromSection}->${fromStep}${fromSubStep ? `->${fromSubStep}` : ""
        } into ${toSection}->${toStep}${toSubStep ? `->${toSubStep}` : ""}`;
    }
  }

  text = `${text}  in ${partNumber}, Rev ${rev}`;

  return text;
};

export const getUnitNameById = (id, units) => {
  let unitName = "";
  units.forEach((unit) => {
    unit.unit_type_for_name.forEach((u) => {
      if (u.unit_id === id) {
        unitName = u.unit_name;
      }
    });
  });
  return unitName;
};

/**
 * Generate alphanumeric suffix (A, B, C, ..., Z, AA, AB, ...)
 * 
 * @param {number} index - The index to convert to alphanumeric suffix
 * @returns {string} - The alphanumeric suffix (e.g., "A", "B", "AA", "AB")
 * 
 * @example
 * generateAlphanumericSuffix(0) // returns "A"
 * generateAlphanumericSuffix(25) // returns "Z"
 * generateAlphanumericSuffix(26) // returns "AA"
 * generateAlphanumericSuffix(27) // returns "AB"
 */
export const generateAlphanumericSuffix = (index) => {
  let result = '';
  let num = index;

  while (num >= 0) {
    result = String.fromCharCode(65 + (num % 26)) + result;
    num = Math.floor(num / 26) - 1;
  }

  return result;
};

/**
 * generateTitlesFromStructure(rootArray)
 * - rootArray: Array of top-level section nodes (parsed JSON)
 * - returns: { [tree_node_id]: generatedTitle }
 *
 * Rules:
 *  - Sections are numbered in document order:  Section 1, Section 2, ...
 *  - Steps are globally numbered across the document: Step 1, Step 2, ...
 *  - Substeps: Substep <StepNumber><AlphaSeq> (A, B, ... Z, AA, AB...)
 *  - Content nodes under steps/substeps are labeled by detected type:
 *      Attachment, Video, Canvas, Rich Text, or generic Item
 *  - Traversal: depth-first pre-order; order is preserved.
 */
export const generateTitlesFromStructure = (rootArray) => {
  if (!Array.isArray(rootArray)) throw new Error('rootArray must be an array of section nodes');

  // helper: 1 -> A, 26 -> Z, 27 -> AA
  function toAlphaSeq(n) {
    let s = '';
    while (n > 0) {
      const rem = (n - 1) % 26;
      s = String.fromCharCode(65 + rem) + s;
      n = Math.floor((n - 1) / 26);
    }
    return s;
  }

  // helper: collect children arrays in the order we expect
  function collectChildrenInOrder(node) {
    const keys = ['steps', 'children', 'substeps'];
    const out = [];
    for (const k of keys) {
      if (Array.isArray(node[k]) && node[k].length) {
        for (const c of node[k]) out.push(c);
      }
    }
    return out;
  }

  // helper: light-weight content type detection (shape-based)
  function detectContentType(node) {
    const candidates = [
      node.type, node.node_type, node.kind, node.component,
      node.detail && node.detail.type, node.detail && node.detail.component,
      node.detail && node.detail.sub_type
    ];
    for (const t of candidates) {
      if (!t) continue;
      const s = String(t).toLowerCase();
      if (s.includes('attach') || s.includes('file')) return 'Attachment';
      if (s.includes('video') || s.includes('youtube') || s.includes('vimeo')) return 'Video';
      if (s.includes('canvas') || s.includes('canva')) return 'Canvas';
      if (s.includes('rich') || s.includes('richtext') || s.includes('rich_text')) return 'Rich Text';
      if (s.includes('image')) return 'Attachment';
    }
    if (node.url || (node.detail && node.detail.url) || node.filename || node.fileName) return 'Attachment';
    if (node.canvas_id || (node.detail && node.detail.canvas_id)) return 'Canvas';
    return 'Item';
  }

  // result object (insertion order follows traversal)
  const result = Object.create(null);

  // counters
  let sectionCounter = 0;
  let stepCounter = 0;
  const substepCountersByStep = Object.create(null); // { [stepNumber]: count }
  const globalContentCounters = Object.create(null); // { Attachment: n, Video: n, ... }

  // iterative DFS stack (seed with root sections in reverse so first element processes first)
  const stack = [];
  for (let i = rootArray.length - 1; i >= 0; i--) {
    stack.push({ node: rootArray[i], parentType: 'root', parentStepNumber: null });
  }

  while (stack.length) {
    const frame = stack.pop();
    const node = frame.node;
    const parentType = frame.parentType;
    const parentStep = frame.parentStepNumber;

    if (!node || !node.tree_node_id) continue;

    const children = collectChildrenInOrder(node);
    const hasChildren = children.length > 0;

    if (parentType === 'root') {
      // Section
      sectionCounter += 1;
      result[node.tree_node_id] = `Section ${sectionCounter}`;

      for (let i = children.length - 1; i >= 0; i--) {
        stack.push({ node: children[i], parentType: 'section', parentStepNumber: null });
      }
      continue;
    }

    if (parentType === 'section') {
      // Step (global)
      stepCounter += 1;
      const thisStepNumber = stepCounter;
      result[node.tree_node_id] = `Step ${thisStepNumber}`;
      substepCountersByStep[thisStepNumber] = 0;

      for (let i = children.length - 1; i >= 0; i--) {
        stack.push({ node: children[i], parentType: 'step', parentStepNumber: thisStepNumber });
      }
      continue;
    }

    if (parentType === 'step') {
      // treat as Substep if either explicit type === 'substep' OR node has children
      if (node.type === 'substep' || hasChildren) {
        const stepNum = parentStep;
        substepCountersByStep[stepNum] = (substepCountersByStep[stepNum] || 0) + 1;
        const alpha = toAlphaSeq(substepCountersByStep[stepNum]);
        result[node.tree_node_id] = `Substep ${stepNum}${alpha}`;

        for (let i = children.length - 1; i >= 0; i--) {
          stack.push({ node: children[i], parentType: 'substep', parentStepNumber: stepNum });
        }
      } else {
        // content directly under step
        const contentType = detectContentType(node);
        globalContentCounters[contentType] = (globalContentCounters[contentType] || 0) + 1;
        result[node.tree_node_id] = `${contentType} ${globalContentCounters[contentType]}`;
      }
      continue;
    }

    if (parentType === 'substep') {
      // nested substep if explicit type === 'substep' OR has children
      if (node.type === 'substep' || hasChildren) {
        const stepNum = parentStep;
        substepCountersByStep[stepNum] = (substepCountersByStep[stepNum] || 0) + 1;
        const alpha = toAlphaSeq(substepCountersByStep[stepNum]);
        result[node.tree_node_id] = `Substep ${stepNum}${alpha}`;

        for (let i = children.length - 1; i >= 0; i--) {
          stack.push({ node: children[i], parentType: 'substep', parentStepNumber: stepNum });
        }
      } else {
        // content within substep
        const contentType = detectContentType(node);
        globalContentCounters[contentType] = (globalContentCounters[contentType] || 0) + 1;
        result[node.tree_node_id] = `${contentType} ${globalContentCounters[contentType]}`;
      }
      continue;
    }

    // fallback: unknown parent type
    if (hasChildren) {
      sectionCounter += 1;
      result[node.tree_node_id] = `Section ${sectionCounter}`;
      for (let i = children.length - 1; i >= 0; i--) {
        stack.push({ node: children[i], parentType: 'section', parentStepNumber: null });
      }
    } else {
      const contentType = detectContentType(node);
      globalContentCounters[contentType] = (globalContentCounters[contentType] || 0) + 1;
      result[node.tree_node_id] = `${contentType} ${globalContentCounters[contentType]}`;
    }
  }

  return result;
}

export const getOriginalNodeName = (tree_node_id, nodeStructure) => {
  let nodeName = nodeStructure[tree_node_id];
  return nodeName ? nodeName : "Invalid Tree Node ID";
}


export const hasModulePermission = (module_type) => {
  const userInfoStr = localStorage.loggedinUserInfo;
  if (!userInfoStr || typeof userInfoStr !== "string") {
    return false;
  }
  let parsed;
  try {
    parsed = JSON.parse(userInfoStr);
  } catch {
    return false;
  }
  const permissions = parsed?.module_type_permissions;
  if (!permissions) {
    return false;
  }
  let hasPermission = false;
  if (module_type) {
    if(module_type in permissions){
      if (permissions[module_type]?.view === true && permissions[module_type]?.edit === true) {
        hasPermission =  true
      }
    }
    else if (permissions["Not Assigned"]?.view === true && permissions["Not Assigned"]?.edit === true) {
      hasPermission = true
    }
  }
  else if (permissions["Not Assigned"]?.view === true && permissions["Not Assigned"]?.edit === true) {
    hasPermission = true
  }

  return hasPermission;
}

/**
 * Redirects to the appropriate view screen for a selected node by finding it in the nodes data structure
 * and determining its parent hierarchy (section, step, substep).
 * 
 * @param {string} tree_node_id - The tree_node_id of the node to redirect to
 * @param {Array} nodesData - Array of section nodes (same structure as nodesdata.json)
 * @param {Function} navigate - React Router navigate function (from useNavigate hook)
 * @param {Object} options - Optional parameters
 * @param {string} options.partNumber - Override part number (if not provided, extracted from first section)
 * @param {string} options.revision - Override revision (if not provided, extracted from first section or defaults to empty)
 * 
 * @example
 * // In a React component:
 * const navigate = useNavigate();
 * RedirectToSelectedNode("1c332e5c-220a-4253-9d2a-17efdccabcc8", nodesData, navigate);
 */
export const RedirectToSelectedNode = (tree_node_id, nodesData, navigate, options = {}) => {
  if (!tree_node_id || !nodesData || !Array.isArray(nodesData) || !navigate) {
    console.error("RedirectToSelectedNode: Missing required parameters");
    return;
  }

  // Helper function to recursively search for a node
  const findNode = (sections, targetId, parentContext = {}) => {
    for (const section of sections) {
      // Check if this section matches
      if (section.tree_node_id === targetId) {
        return {
          node: section,
          type: "section",
          parentContext: {},
        };
      }

      // Search in steps
      if (section.steps && Array.isArray(section.steps)) {
        for (const step of section.steps) {
          // Check if this step matches
          if (step.tree_node_id === targetId) {
            return {
              node: step,
              type: "step",
              parentContext: {
                sectionId: section.tree_node_id,
              },
            };
          }

          // Search in substeps
          if (step.substeps && Array.isArray(step.substeps)) {
            for (const substep of step.substeps) {
              // Check if this substep matches
              if (substep.tree_node_id === targetId) {
                return {
                  node: substep,
                  type: "substep",
                  parentContext: {
                    sectionId: section.tree_node_id,
                    stepId: step.tree_node_id,
                  },
                };
              }

              // Search in substep children
              if (substep.children && Array.isArray(substep.children)) {
                for (const child of substep.children) {
                  // For canvas nodes, check both tree_node_id and canvas_tree_node_id
                  if (child.type === "canva") {
                    const canvasNodeId = child.detail?.canvas_tree_node_id || child.tree_node_id;
                    if (child.tree_node_id === targetId || canvasNodeId === targetId) {
                      return {
                        node: child,
                        type: "canva",
                        parentContext: {
                          sectionId: section.tree_node_id,
                          stepId: step.tree_node_id,
                          substepId: substep.tree_node_id,
                        },
                      };
                    }
                  } else if (child.tree_node_id === targetId) {
                    return {
                      node: child,
                      type: child.type,
                      parentContext: {
                        sectionId: section.tree_node_id,
                        stepId: step.tree_node_id,
                        substepId: substep.tree_node_id,
                      },
                    };
                  }
                }
              }
            }
          }

          // Search in step children
          if (step.children && Array.isArray(step.children)) {
            for (const child of step.children) {
              // For canvas nodes, check both tree_node_id and canvas_tree_node_id
              if (child.type === "canva") {
                const canvasNodeId = child.detail?.canvas_tree_node_id || child.tree_node_id;
                if (child.tree_node_id === targetId || canvasNodeId === targetId) {
                  return {
                    node: child,
                    type: "canva",
                    parentContext: {
                      sectionId: section.tree_node_id,
                      stepId: step.tree_node_id,
                    },
                  };
                }
              } else if (child.tree_node_id === targetId) {
                return {
                  node: child,
                  type: child.type,
                  parentContext: {
                    sectionId: section.tree_node_id,
                    stepId: step.tree_node_id,
                  },
                };
              }
            }
          }
        }
      }
    }
    return null;
  };

  // Find the node
  const result = findNode(nodesData, tree_node_id);

  if (!result) {
    console.error(`RedirectToSelectedNode: Node with tree_node_id "${tree_node_id}" not found`);
    return;
  }

  const { node, type, parentContext } = result;

  // Extract part information from the first section (all sections should have same part_ref and procedure_rev_ref)
  const firstSection = nodesData[0];
  if (!firstSection) {
    console.error("RedirectToSelectedNode: No sections found in nodesData");
    return;
  }

  const partRef = firstSection.part_ref;
  const procedureRevRef = firstSection.procedure_rev_ref;
  const partNumber = options.partNumber || firstSection.detail?.part_details?.part_number || "";
  // Revision might not be in the data structure, so allow override or default to empty
  const revision = options.revision || "";

  // Build URL based on node type
  let url = "";

  switch (type) {
    case "section":
      url = `/view-section/${partNumber}/${revision}/${partRef}/${procedureRevRef}/${node.tree_node_id}`;
      break;

    case "step":
      url = `/view-step/${partNumber}/${revision}/${partRef}/${procedureRevRef}/${parentContext.sectionId}/${node.tree_node_id}`;
      break;

    case "substep":
      url = `/view-substep/${partNumber}/${revision}/${partRef}/${procedureRevRef}/${parentContext.sectionId}/${parentContext.stepId}/${node.tree_node_id}`;
      break;

    case "rich-text":
    case "video":
    case "form":
    case "attachment":
      // For child nodes
      const baseUrl = `/view-childnode/${partNumber}/${revision}/${partRef}/${procedureRevRef}/${parentContext.sectionId}/${parentContext.stepId}/${node.tree_node_id}`;
      // Add substep_id_ref as query parameter if it exists
      url = parentContext.substepId
        ? `${baseUrl}?substep_id_ref=${parentContext.substepId}`
        : baseUrl;
      break;

    case "canva":
      // For canvas elements, use detail.canvas_tree_node_id instead of tree_node_id
      const canvasNodeId = node.detail?.canvas_tree_node_id || node.tree_node_id;
      const canvasBaseUrl = `/view-childnode/${partNumber}/${revision}/${partRef}/${procedureRevRef}/${parentContext.sectionId}/${parentContext.stepId}/${canvasNodeId}`;
      // Add substep_id_ref as query parameter if it exists
      url = parentContext.substepId
        ? `${canvasBaseUrl}?substep_id_ref=${parentContext.substepId}`
        : canvasBaseUrl;
      break;

    default:
      return;
  }

  // Navigate to the URL
  navigate(url);
};

/**
 * Handles redirect to copied/moved node after a successful copyElement API call.
 * This is a reusable function that can be used in both CopyReplace and MoveReplace components.
 * 
 * @param {Object} response - The API response from copyElement call
 * @param {Object} targetDocument - Target document object with partId, revisionId, partNumber, revision
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} navigate - React Router navigate function (from useNavigate hook)
 * @param {Function} getRevAllNodes - Redux action to fetch all nodes (from procedureRevisionSlice)
 * @param {Function} setLoading - Redux action creator to set loading state (e.g., setLoading or setMoveLoading)
 * 
 * @returns {Promise<boolean>} - Returns true if redirect was successful, false otherwise
 * 
 * @example
 * // In a React component:
 * const response = await dispatch(copyElement(payload)).unwrap();
 * const redirected = await handleRedirectToCopiedNode(
 *   response,
 *   targetDocument,
 *   dispatch,
 *   navigate,
 *   getRevAllNodes,
 *   setLoading
 * );
 * if (redirected) {
 *   return; // Exit early, redirect already happened
 * }
 * // Continue with default navigation if redirect didn't happen
 */
export const handleRedirectToCopiedNode = async (
  response,
  targetDocument,
  dispatch,
  navigate,
  getRevAllNodes,
  setLoading
) => {
  if (!response || !response.success) {
    return false;
  }

  if (!targetDocument || !targetDocument.partId || !targetDocument.revisionId) {
    console.error("handleRedirectToCopiedNode: Missing target document information");
    return false;
  }

  if (!dispatch || !navigate || !getRevAllNodes) {
    console.error("handleRedirectToCopiedNode: Missing required functions");
    return false;
  }

  try {
    // Set loading to true at the start of redirect process
    if (setLoading) {
      dispatch(setLoading(true));
    }


    const copiedTreeNodeId = Object.values(response.data.tree_nodes[0])[0];
    const reference = response.data.ref_nodes[0].reference;

    if (!copiedTreeNodeId) {
      if (setLoading) {
        dispatch(setLoading(false));
      }
      return false;
    }

    // Fetch all nodes from target document to get the full structure
    const nodesDataResult = await dispatch(getRevAllNodes({
      partId: targetDocument.partId,
      revisionId: targetDocument.revisionId
    })).unwrap();

    if (!nodesDataResult || !Array.isArray(nodesDataResult)) {
      if (setLoading) {
        dispatch(setLoading(false));
      }
      return false;
    }

    // Redirect to the copied node
    RedirectToSelectedNode(
      copiedTreeNodeId,
      nodesDataResult,
      navigate,
      {
        partNumber: targetDocument.partNumber,
        revision: targetDocument.revision
      }
    );

    // Check if tasks are copied to the node
    const { fetchtAllTasks, setCopiedAndRedirect, openTaskPopup } = require('../redux/taskSlice');
    const tasksResponse = await dispatch(fetchtAllTasks({searchQuery:`limit=10&offset=0&reference=${reference}`})).unwrap();
    if (tasksResponse?.results?.length > 0) {                  
      // Set copied and redirected flag in Redux
      dispatch(setCopiedAndRedirect(true));
  
      // Open task popup to show the notification
      dispatch(openTaskPopup());
    }

    // Set loading to false before navigation (component will unmount)
    if (setLoading) {
      dispatch(setLoading(false));
    }

    return true; // Redirect was successful
  } catch (error) {
    console.error("handleRedirectToCopiedNode: Error redirecting to copied node:", error);
    // Set loading to false on error
    if (setLoading) {
      dispatch(setLoading(false));
    }
    return false; // Redirect failed
  }
};


/**
 * Get open tasks from tasks response
 * 
 * @param {*} tasksResponse 
 * @returns 
 */
export const getOpenTasks = (tasksResponse) => {
  return (tasksResponse?.results || []).filter(
    (task) => task.status !== "COMPLETED" && task.status !== "CANCELLED"
  );
};
export const setupApplicationSetup = async (data) => {
  const latestAppSetup = data.reduce((latest, current) => {
    const latestDate = new Date(latest.updated_at || latest.created_at || 0);
    const currentDate = new Date(current.updated_at || current.created_at || 0);
    return currentDate > latestDate ? current : latest;
  });
  let authMethods = null;
  localStorage.setItem("application_time_zone",latestAppSetup.application_time_zone);
  if (latestAppSetup?.signature_auth_methods) {
    authMethods = latestAppSetup.signature_auth_methods;
    // Store in separate key for backward compatibility
    localStorage.setItem(
      "signature_auth_methods",
      JSON.stringify(authMethods)
    );

    // Also update in loggedinUserInfo if it exists
    const loggedinUserInfo = localStorage.getItem("loggedinUserInfo");
    if (loggedinUserInfo) {
      try {
        const userInfo = JSON.parse(loggedinUserInfo);
        userInfo.signature_auth_methods = authMethods;
        localStorage.setItem("loggedinUserInfo", JSON.stringify(userInfo));
      } catch (error) {
        console.error(
          "Error updating loggedinUserInfo with auth methods:",
          error
        );
      }
    }
  }
}


export const uploadJsonToS3 = async (jsonData, part_number, revision,addTimeStamp = false) => {
  try {
    const fileName = `${part_number}_${revision}${addTimeStamp ? `_${Date.now()}` : ''}.json`;
    const jsonString = JSON.stringify(jsonData);
    // Encode to base64, handling UTF-8 characters
    const base64 = window.btoa(unescape(encodeURIComponent(jsonString)));
    const dataUri = `data:application/json;base64,${base64}`;

    const url = await uploadToS3(dataUri, fileName, "application/json","revision-snap",false);
    return url;
  } catch (error) {
    console.error("Error uploading JSON to S3:", error);
    throw error;
  }
};

/**
 * Adjusts sibling order of existing nodes and inserts the new node.
 * 
 * @param {Array} childrenArray - The array of sibling nodes.
 * @param {Object} newNode - The new node to insert.
 */
export const adjustSiblingOrderAndInsert = (childrenArray, newNode) => {
  if (!Array.isArray(childrenArray) || !newNode) return;

  childrenArray.forEach((child) => {
    if (Number(child.sibling_order) >= Number(newNode.sibling_order)) {
      child.sibling_order = (Number(child.sibling_order) + 1).toString();
      if (child.procedure_tree) {
        child.procedure_tree.sibling_order = child.sibling_order;
      }
    }
  });

  childrenArray.splice(Number(newNode.sibling_order) - 1, 0, newNode);
};

export const prepareCurrentSnapshot = ({allNodesData,part_number,procedure_rev,procedure_status, item_code_type,partDescription="",part_ref, procedure_rev_ref}) => {
  const snapshotPayload = {
    data:allNodesData,
    message: "Procedure tree fetched successfully",
    part_number: part_number,
    procedure_rev:procedure_rev,
    procedure_status:procedure_status,
    item_code_type:item_code_type,
    partDescription:partDescription,
    success: true,
    part_ref, 
    procedure_rev_ref
  };

  return snapshotPayload;
}

export const getRevAndTraining = async (dispatch, partId) => {
  try {
    const { getTrainingLogForPart, getPartMentionsRevisionsForPart } = require('../redux/procedureRevisionSlice');
    const [trainingLogsResult, revisionsResult] = await Promise.all([
      dispatch(getTrainingLogForPart({ partId })).unwrap(),
      dispatch(getPartMentionsRevisionsForPart({ partId})).unwrap(),
    ]);

    return {
      present_training_logs: trainingLogsResult.results,
      present_revisions: revisionsResult.results,
    };
  } catch (error) {
    console.error("getRevAndTraining: Error fetching data:", error);
    return {
      present_training_logs: [],
      present_revisions: [],
    };
  }
};


export const getBomRows = (allNodesData = []) => {
    const mergeBomRow = (bomAggregator, row) => {
      const partNumber =
        row.partNumber || row.part_number || row.part_info?.part_number;
      if (!partNumber) return;

      const qty = parseFloat(row.qty ?? 0);
      const typeName =
        row.type || row.type_name || row.part_info?.type_info?.name || "";
      const description =
        row.description ||
        row.part_description ||
        row.part_info?.description ||
        "";
      const unitName =
        row.unit_name || row.part_info?.unit_info?.unit_name || "";
      const orders =
        row.type_orders ??
        row.part_info?.type_info?.orders ??
        row.orders ??
        row.part_info?.part_info?.type_info?.orders;

      if (!bomAggregator[partNumber]) {
        bomAggregator[partNumber] = {
          type_name: typeName,
          part_number: partNumber,
          part_description: description,
          qty,
          unit_name: unitName,
          orders,
        };
      } else {
        bomAggregator[partNumber].qty += qty;
      }
    };

    const aggregateFromNodes = (nodes) => {
      if (!Array.isArray(nodes)) return [];

      const bomAggregator = {};

      const traverse = (nodeList) => {
        if (!Array.isArray(nodeList)) return;
        nodeList.forEach((node) => {
          const pastData = node.detail?.past_step_data_json;
          if (pastData?.bom_rows && Array.isArray(pastData.bom_rows)) {
            pastData.bom_rows.forEach((row) => mergeBomRow(bomAggregator, row));
          }
          const partsReq = node.detail?.step_data?.parts_required;
          if (partsReq && Array.isArray(partsReq)) {
            partsReq.forEach((row) => mergeBomRow(bomAggregator, row));
          }
          if (node.steps) traverse(node.steps);
          if (node.substeps) traverse(node.substeps);
        });
      };

      traverse(nodes);
      return Object.values(bomAggregator);
    };


    return aggregateFromNodes(allNodesData);
  }